import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import _get from 'lodash/get';
import { makeSelectHasWrappedSummary } from 'containers/Account/selectors';
import Mixpanel from 'utils/mixpanelService';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { useHistory } from 'react-router-dom';
import { isSessionStorageAllowed } from 'utils/stringUtils';
import ArrowRight from './icons/WrappedArrowRight';
import CloseIcon from './icons/WrappedCloseIcon';
import Brain from './icons/Brain';

const useStyles = makeStyles({
  wrapper: props => ({
    position: 'fixed',
    zIndex: 1301,
    ...(props.fromMobile
      ? {
          bottom: 85,
          right: 10,
        }
      : {
          top: 'auto',
          left: 'auto',
        }),
  }),
  arrow: props => ({
    position: 'absolute',
    fontSize: 7,
    width: '3em',
    height: '3em',
    ...(props.fromMobile
      ? {
          bottom: 0,
          right: 24,
          marginBottom: '-0.71em',
          width: '1em',
          height: '1em',
          '&::before': {
            content: '""',
            margin: 'auto',
            display: 'block',
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: '1em 1em 0 1em',
            borderColor: '#0862C0 transparent transparent transparent',
          },
        }
      : {
          top: 0,
          left: '47%',
          marginTop: '-0.71em',
          marginLeft: '-0.71em',
          width: '1em',
          height: '1em',
          '&::before': {
            content: '""',
            margin: 'auto',
            display: 'block',
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: '0 1em 1em 1em',
            borderColor: 'transparent transparent #0862C0 transparent',
          },
        }),
  }),
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: props => (props.fromMobile ? 2 : 8),
    backgroundColor: '#0862C0',
    borderRadius: 8,
    padding: props => (props.fromMobile ? '16px 8px' : '20px 8px'),
    maxWidth: 158,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#FFFFFF',
    '&:hover': {
      opacity: 0.8,
    },
  },
  closeIcon: {
    fontSize: 20,
    fontWeight: 300,
    lineHeight: 1,
  },
  title: {
    fontFamily: 'Mada',
    fontSize: 14,
    fontWeight: 400,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  brainIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: props => (props.fromMobile ? 0 : 4),
  },
  discoverButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    backgroundColor: '#FFFFFF',
    border: '1px solid #0862C0',
    borderRadius: 8,
    padding: '8px 12px',
    cursor: 'pointer',
    fontFamily: 'Mada',
    fontSize: 12,
    fontWeight: 400,
    color: '#0862C0',
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
});

const WrappedNotification = ({ anchorEl, fromMobile }) => {
  const classes = useStyles({ fromMobile });
  const history = useHistory();
  const hasWrappedSummary = useSelector(makeSelectHasWrappedSummary());
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const arrowRef = useRef(null);
  const wrapperRef = useRef(null);
  const [wrappedPageCopy] = useSiteCopySelector(['wrapped-2025']);
  const pageCopy = _get(wrappedPageCopy, 'pageCopy', {});

  // Calculate position for desktop version
  useEffect(() => {
    if (!fromMobile && anchorEl && isVisible) {
      const updatePosition = () => {
        if (anchorEl && wrapperRef.current) {
          const rect = anchorEl.getBoundingClientRect();
          const wrapperRect = wrapperRef.current.getBoundingClientRect();
          setPosition({
            top: rect.bottom + 8,
            left: rect.left + rect.width / 2 - wrapperRect.width / 2,
          });
        }
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      return () => window.removeEventListener('resize', updatePosition);
    }
    return undefined;
  }, [anchorEl, isVisible, fromMobile]);

  useEffect(() => {
    // Check if notification was already shown in this session
    if (isSessionStorageAllowed()) {
      const shownInSession = sessionStorage.getItem(
        'wrapped2025_notification_shown',
      );
      if (shownInSession === 'true') {
        setIsDismissed(true);
        return;
      }
    }

    // Show notification if wrapped summary is available
    // Show only once per session
    if (hasWrappedSummary) {
      setIsVisible(true);
      // Mark as shown in this session
      if (isSessionStorageAllowed()) {
        sessionStorage.setItem('wrapped2025_notification_shown', 'true');
      }
    }
  }, [hasWrappedSummary]);

  const handleClose = () => {
    setIsVisible(false);
    setIsDismissed(true);
    Mixpanel.track('Wrapped Notification - Dismissed');
  };

  const handleDiscover = () => {
    setIsVisible(false);
    setIsDismissed(true);
    Mixpanel.track('Wrapped Notification - Discover Clicked');
    history.push('/account/recommendations');
  };

  const isRecommendationsPage = window.location?.pathname?.includes(
    '/account/recommendations',
  );

  if (
    !isVisible ||
    isDismissed ||
    !hasWrappedSummary ||
    !anchorEl ||
    isRecommendationsPage
  ) {
    return null;
  }

  const wrapperStyle = fromMobile
    ? {}
    : {
        top: `${position.top}px`,
        left: `${position.left}px`,
      };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box ref={wrapperRef} className={classes.wrapper} style={wrapperStyle}>
        <Box className={classes.container} position="relative">
          <span className={classes.arrow} ref={arrowRef} />
          <ButtonBase
            className={classes.closeButton}
            onClick={handleClose}
            aria-label="Close notification"
          >
            <CloseIcon />
          </ButtonBase>

          <Box className={classes.brainIcon}>
            <Brain />
          </Box>

          <Typography className={classes.title}>
            {_get(pageCopy, 'notificationTitle')}
          </Typography>

          <ButtonBase
            className={classes.discoverButton}
            onClick={handleDiscover}
            aria-label="Discover Your Wellbeing Wrapped"
          >
            DISCOVER
            <ArrowRight />
          </ButtonBase>
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export default WrappedNotification;
